import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../../src/lib/supabase';
import { CobrancaCliente } from '../../../../types';
import { ClienteConsolidado } from '../types';

/**
 * Estado da esteira de cobrança dos clientes listados, indexado por CPF/CNPJ.
 *
 * Vem de `cobranca_clientes`, que a Central de Inadimplência nunca consultou:
 * 80 dos 85 devedores antigos JÁ tinham linha lá — com responsável, status e
 * histórico — e nada disso aparecia na tela. A chave é o documento resolvido
 * pelo cadastro da UC (ver fetchCadastroUcs no hook de dados), porque nas
 * faturas antigas `cliente_cpf_cnpj` está vazio.
 *
 * Usado por "Débitos antigos" e por "Vencidas 15+ dias" — o operador precisa
 * enxergar em qual pé está a negociação antes de abrir o gerenciamento.
 */
export function useEsteiraCobranca(cpfs: string[]) {
  const [esteira, setEsteira] = useState<Map<string, CobrancaCliente>>(new Map());
  const [carregando, setCarregando] = useState(false);

  // Dependência primitiva: o array é recriado a cada render do pai e
  // dispararia o efeito indefinidamente.
  const chave = cpfs.join(',');

  useEffect(() => {
    const lista = chave ? chave.split(',') : [];
    if (lista.length === 0) {
      setEsteira(new Map());
      return;
    }
    let cancelado = false;
    (async () => {
      setCarregando(true);
      try {
        const mapa = new Map<string, CobrancaCliente>();
        const lote = 200; // teto prático da querystring do PostgREST
        for (let i = 0; i < lista.length; i += lote) {
          const { data, error } = await supabase
            .from('cobranca_clientes')
            .select('*')
            .in('cpf_cnpj_cliente', lista.slice(i, i + lote));
          if (error) throw error;
          (data || []).forEach((c: any) => mapa.set(c.cpf_cnpj_cliente, c as CobrancaCliente));
        }
        if (!cancelado) setEsteira(mapa);
      } catch (err) {
        console.error('Erro ao carregar esteira de cobrança:', err);
      } finally {
        if (!cancelado) setCarregando(false);
      }
    })();
    return () => { cancelado = true; };
  }, [chave]);

  return { esteira, carregando };
}

/** Lista de CPF/CNPJ distintos de um conjunto de clientes, memoizada. */
export function useCpfsDosClientes(clientes: ClienteConsolidado[]) {
  return useMemo(
    () => Array.from(new Set(clientes.map(c => c.cpfCnpj).filter((v): v is string => !!v))),
    [clientes]
  );
}
